import { BuilderBlocks } from "@builder.io/react";
import { BuilderElement } from "@builder.io/sdk";
import { Core } from "@faire/design-tokens";
import { Column } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import React, { useState } from "react";

import { BuilderAdditionalProps, BuilderAttributes } from "./BuilderTypes";

// copied from @faire/deprecated-retailer-visitor-shared/lib/handleKeyDown
// to avoid the dependency on the shared library
const handleKeyDown =
  (onContinue: () => void) => (event: React.KeyboardEvent) => {
    const keyCode = event.keyCode || event.key;

    if ((keyCode === 13 || keyCode === "Enter") && !event.repeat) {
      event.preventDefault();
      onContinue();
    }
  };

type TabItem = {
  label: string;
  content: BuilderElement[];
};

export interface BuilderTabsProps extends BuilderAdditionalProps {
  tabs?: TabItem[];
  attributes: BuilderAttributes;
  defaultActiveTab?: number;
  tabHeaderLayout?: "flex-start" | "flex-end" | "center";
  activeTabStyle?: React.CSSProperties;
}

export const BuilderTabs: React.FC<BuilderTabsProps> = ({
  tabs = [],
  attributes,
  defaultActiveTab = 0,
  tabHeaderLayout = "flex-start",
  activeTabStyle,
}) => {
  const [activeTab, setActiveTab] = useState(defaultActiveTab);

  const currentTab = tabs[activeTab];

  const handleClickTab = (index: number) => {
    setActiveTab(index);
  };

  return (
    <Column
      gap="2x"
      {...attributes}
      className={cn("items-start lg:items-center")}
    >
      <div
        className="flex w-full flex-wrap justify-start gap-2 py-2"
        style={{
          justifyContent: tabHeaderLayout,
        }}
      >
        {tabs.map((tab, index) => (
          <div
            key={index}
            tabIndex={0}
            onClick={() => handleClickTab(index)}
            onKeyDown={handleKeyDown(() => handleClickTab(index))}
            className={cn(
              "flex h-[40px] cursor-pointer items-center justify-center rounded-[40px] border p-4",
              index === activeTab
                ? "bg-fs-surface-primary-inverse border-fs-action-border-default"
                : "border-fs-border-muted hover:border-fs-action-border-default focus-visible:border-fs-action-border-default bg-transparent"
            )}
            style={index === activeTab ? activeTabStyle : undefined}
          >
            <Typography
              color={
                index === activeTab ? Core.text.inverse : Core.text.default
              }
            >
              {tab.label}
            </Typography>
          </div>
        ))}
      </div>

      {currentTab ? (
        <div className="relative w-full items-start lg:items-center">
          <BuilderBlocks
            dataPath={`component.options.tabs.${activeTab}.content`}
            blocks={currentTab.content}
          />
        </div>
      ) : null}
    </Column>
  );
};
